// PACOTE DE EXEMPLO — os tres arquivos do ciclo, preenchidos.
//
// Existe por causa de uma queixa do cliente em 21/07/2026: "o exemplo baixado
// nao e o mesmo, nao segue a mesma tabela". Na epoca a resposta foi tirar a
// linha de exemplo e entregar so o cabecalho — honesto, mas quem precisa
// entender o layout ficou sem nada pra olhar.
//
// A causa do problema nao era ter exemplo: era o exemplo ser escrito a mao,
// separado da definicao. Aqui as tres coisas que garantem que isso nao repita:
//
//   1) a ORDEM das colunas sai dos mesmos arrays `COLUNAS_*` que o parser usa;
//   2) os valores saem na forma que o ERP real produz (`000000000420,00`), nao
//      na forma canonica interna — o exemplo tem que PARECER com o arquivo dele;
//   3) um teste passa este pacote pelo parser DE VERDADE e exige zero rejeicao.
//      Exemplo que o proprio sistema recusaria e pior que exemplo nenhum.
//
// Os dados sao ficticios e escolhidos pra ensinar, nao pra encher linha: cada
// servidor existe por um motivo, e o motivo esta na tabela do doc tecnico.
import { COLUNAS_CARGA, COLUNAS_CONSIGNADOS } from "./retorno.js";
import { COLUNAS_RUBRICA } from "./rubrica.js";
export const EXEMPLO_COMPETENCIA = "202608";
/**
 * Junta as celulas na ordem que o array de colunas declara, SEM reformatar.
 *
 * Nao usa `montarLinhaDelimitada` de proposito: aquele normaliza dinheiro pra
 * `420.00`, e aqui o objetivo e' o oposto — sair na forma do arquivo real
 * (`000000000420,00`), que o parser tambem aceita e que o operador reconhece.
 * A ordem, que e' o que dava errado antes, continua vindo do array.
 */
function linhaExemplo(campos, valores) {
    const max = campos.reduce((m, c) => Math.max(m, c.coluna), 0);
    const celulas = Array.from({ length: max }, () => "");
    for (const campo of campos) {
        const v = valores[campo.nome];
        if (v !== undefined)
            celulas[campo.coluna - 1] = v;
    }
    return celulas.join(";");
}
/** Valor na forma do arquivo real: 15 posicoes, zeros a esquerda, virgula. */
function dinheiroErp(reais) {
    const centavos = Math.round(reais * 100);
    const inteiro = Math.trunc(centavos / 100);
    const resto = String(centavos % 100).padStart(2, "0");
    return `${String(inteiro).padStart(12, "0")},${resto}`;
}
/** Tres digitos, zeros a esquerda — como as colunas de parcela vem no arquivo. */
function tres(n) {
    return String(n).padStart(3, "0");
}
/**
 * CPF de digito REPETIDO — invalido de proposito.
 *
 * Segue a decisao que `apps/api/scripts/gerar-carga-exemplo.mjs` ja tomava:
 * "e arquivo de exemplo, nao pode carregar dado de pessoa". Um CPF com digito
 * verificador valido é o CPF de alguem, e este arquivo vai ser distribuido pra
 * prefeitura, pra empresa que mexe na folha dela e pra quem mais precisar.
 *
 * Nao quebra nada: a coluna de CPF do layout e `kind: "alfa"`, e `alfa` nao
 * valida — ver `csv-delimitado.ts`. Sequencia de digito repetido nunca passa em
 * validador de CPF, entao tambem nao ha risco de alguem levar o exemplo pra
 * producao por engano e o registro passar.
 */
function cpfExemplo(indice) {
    return String(indice % 10).repeat(11);
}
export const SERVIDORES_EXEMPLO = [
    {
        matricula: "9000001", nome: "ANA EXEMPLO DA SILVA", situacao: "AT",
        vinculo: "EFETIVO", cargo: "AGENTE ADMINISTRATIVO", dataAdmissao: "05032015",
        salarioLiquido: 4200, salarioNoHolerite: 5200,
        motivo: "O caso comum: duas dívidas em **baldes diferentes** — empréstimo e cartão. Serve para ver que os tetos são independentes.",
    },
    {
        matricula: "9000002", nome: "BRUNO EXEMPLO SOUZA", situacao: "AT",
        vinculo: "EFETIVO", cargo: "MOTORISTA", dataAdmissao: "12082018",
        salarioLiquido: 3100, salarioNoHolerite: 3900,
        motivo: "Duas dívidas **na mesma verba**, distinguidas só pelo valor. É o caso que torna a referência (ADF) útil: sem ela, o casamento depende do valor.",
    },
    {
        matricula: "9000003", nome: "CARLA EXEMPLO LIMA", situacao: "LI",
        vinculo: "EFETIVO", cargo: "PROFESSORA", dataAdmissao: "01022012",
        salarioLiquido: 0, salarioNoHolerite: 2800,
        motivo: "Licença. O salário vai **zerado na carga de propósito** e a Atlas não o compara — em `LI` e `CE` não houve holerite comparável. A dívida continua descontando.",
    },
    {
        matricula: "9000004", nome: "DANIEL EXEMPLO ROCHA", situacao: "DE",
        vinculo: "COMISSIONADO", cargo: "ASSESSOR", dataAdmissao: "02012023",
        salarioLiquido: 2800, salarioNoHolerite: 3500,
        motivo: "**Desligamento declarado.** É esta linha que tira os contratos da folha e notifica os bancos. Ausência do arquivo não faz isso.",
    },
    {
        matricula: "9000005", nome: "ELISA EXEMPLO PINTO", situacao: "AT",
        vinculo: "EFETIVO", cargo: "FISCAL", dataAdmissao: "18092019",
        salarioLiquido: 0, salarioNoHolerite: 0,
        motivo: "Ativa, mas com folha **zerada** — afastamento que o arquivo não nomeia. Sem dívida e sem desconto. É o caso em que ausência de desconto significa suspensão, não quitação.",
    },
];
export const VERBAS_EXEMPLO = [
    { codigo: "0001", descricao: "SALARIO BASE", tipo: "P", natureza: "**provento** — não gera dívida" },
    { codigo: "0197", descricao: "IPREMN", tipo: "D", natureza: "**desconto que não é consignação** (previdência municipal). Vem como `D` e não pode virar dívida." },
    { codigo: "0246", descricao: "SINDSEP", tipo: "D", natureza: "**desconto que não é consignação** (sindicato). Também `D`." },
    { codigo: "0234", descricao: "DESCONTO EMP CONSIG BB", tipo: "D", natureza: "**consignação**, balde de empréstimo (35%)" },
    { codigo: "0267", descricao: "CONSIG. BRADESCO", tipo: "D", natureza: "**consignação**, balde de empréstimo (35%)" },
    { codigo: "0041", descricao: "SAQUE ANTECIPCARD", tipo: "D", natureza: "**consignação**, balde de **cartão** (5%) — o texto sugere empréstimo e um classificador automático erraria, trocando o teto de 5% pelo de 35%" },
];
/**
 * Descontos do exemplo. `parcelasRestantes` NAO esta aqui: e' derivado
 * (`total - atual + 1`), porque escrever a mao seria a chance de o exemplo
 * contradizer a regra que o documento ensina.
 */
const DESCONTOS_EXEMPLO = [
    { matricula: "9000001", verba: "0234", valor: 420, parcelaAtual: 7, parcelasTotal: 48 },
    { matricula: "9000001", verba: "0041", valor: 105, parcelaAtual: 1, parcelasTotal: 18 },
    { matricula: "9000002", verba: "0267", valor: 180, parcelaAtual: 13, parcelasTotal: 32 },
    { matricula: "9000002", verba: "0267", valor: 240, parcelaAtual: 19, parcelasTotal: 48 },
    { matricula: "9000003", verba: "0234", valor: 150, parcelaAtual: 50, parcelasTotal: 60 },
    { matricula: "9000004", verba: "0234", valor: 200, parcelaAtual: 32, parcelasTotal: 36 },
];
/** Parcelas que faltam, INCLUINDO a do mes. Mesma regra do layout. */
export function restantesExemplo(d) {
    return d.parcelasTotal - d.parcelaAtual + 1;
}
export function exemploCarga(competencia = EXEMPLO_COMPETENCIA) {
    return (SERVIDORES_EXEMPLO.map((s, i) => linhaExemplo(COLUNAS_CARGA, {
        competencia,
        matricula: s.matricula,
        cpf: cpfExemplo(i + 1),
        nome: s.nome,
        situacao: s.situacao,
        vinculo: s.vinculo,
        cargo: s.cargo,
        dataAdmissao: s.dataAdmissao,
        salarioLiquido: dinheiroErp(s.salarioLiquido),
        bancoCodigo: "001",
        agencia: "1234",
        conta: `000${s.matricula.slice(-4)}-0`,
    })).join("\r\n") + "\r\n");
}
export function exemploConsignados() {
    const desc = (verba) => VERBAS_EXEMPLO.find((v) => v.codigo === verba)?.descricao ?? "";
    return (DESCONTOS_EXEMPLO.map((d) => linhaExemplo(COLUNAS_CONSIGNADOS, {
        matricula: d.matricula,
        codigoVerba: d.verba,
        descricao: desc(d.verba),
        tipo: "D",
        valor: dinheiroErp(d.valor),
        parcelasRestantes: tres(restantesExemplo(d)),
        // Vazia de proposito: e' assim que o arquivo real chega (151 de 151
        // linhas medidas), e e' o que faz o casamento por atributos ser o
        // caminho normal em vez da excecao.
        referencia: "",
        parcelaAtual: tres(d.parcelaAtual),
        parcelasTotal: tres(d.parcelasTotal),
    })).join("\r\n") + "\r\n");
}
export function exemploRubricas(competencia = EXEMPLO_COMPETENCIA) {
    const linhas = [];
    for (const s of SERVIDORES_EXEMPLO) {
        const por = (codigo, valor) => {
            const v = VERBAS_EXEMPLO.find((x) => x.codigo === codigo);
            linhas.push(linhaExemplo(COLUNAS_RUBRICA, {
                competencia,
                matricula: s.matricula,
                codigoVerba: v.codigo,
                descricao: v.descricao,
                tipo: v.tipo,
                valor: dinheiroErp(valor),
            }));
        };
        // O holerite traz TODAS as verbas, provento incluso — e' o que permite
        // descobrir o catalogo e conferir o desconto.
        por("0001", s.salarioNoHolerite);
        if (s.salarioNoHolerite > 0) {
            por("0197", Math.round(s.salarioNoHolerite * 0.11 * 100) / 100);
            if (s.matricula === "9000002")
                por("0246", 39);
        }
        // Cada consignacao do consignados aparece aqui com o MESMO valor: e' o
        // cruzamento que confirma que a folha executou o que instruiu.
        for (const d of DESCONTOS_EXEMPLO.filter((x) => x.matricula === s.matricula)) {
            por(d.verba, d.valor);
        }
    }
    return linhas.join("\r\n") + "\r\n";
}
/** Nomes dentro do zip — os que o leitor de pacote reconhece. */
export const EXEMPLO_NOMES = {
    carga: "arquivocarga.csv",
    consignados: "retorno-consignados.csv",
    rubricas: (competencia = EXEMPLO_COMPETENCIA) => 
    // Sem zero no mes, como o pacote real: `rubrica_funcionarios20268`.
    `rubrica_funcionarios${competencia.slice(0, 4)}${Number(competencia.slice(4))}.csv`,
};
//# sourceMappingURL=exemplo-pacote.js.map